import * as url from "helpers/url_helper";
import { APIClient } from "helpers/api_helper";
import axios from "axios";

const api = new APIClient();

// Create a separate axios instance for import operations to bypass interceptors
const importAxios = axios.create();

// Add request interceptor for authentication
importAxios.interceptors.request.use((config) => {
  return config;
});

// Payments
export const getPayments = (params: any) => api.get(url.GET_PAYMENTS, params);
export const getPayment = (id: any, params: any) => {
  const populate: any = [
    "individual",
    "mda",
    "mda.region",
    "agency",
    "positionType",
    "paymentCategory",
    "thematicArea",
    "sector",
    "createdby",
    "updatedby",
    "createdby.agency",
    "updatedby.agency",
  ];
  if (!params?.populate) params.populate = populate;
  return api.get(url.GET_PAYMENT + "/" + id, params);
};
export const addPayment = (data: any) => api.create(url.ADD_PAYMENT, data);
export const importPayment = (
  data: any,
  accessToken: string,
  options?: { timeoutMs?: number }
) => {
  return importAxios
    .post(process.env.REACT_APP_API_URL + url.IMPORT_PAYMENT, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      timeout: options?.timeoutMs ?? 5 * 60 * 1000,
      responseType: "blob",
    })
    .then((response) => {
      // Get the X-Metadata header if it exists
      const metadata = response.headers["x-metadata"];
      let parsedMetadata = null;

      // Try to parse the metadata if it exists
      if (metadata) {
        try {
          parsedMetadata = JSON.parse(metadata);
        } catch (e) {
          parsedMetadata = metadata;
        }
      }

      // Return both the blob and metadata
      return {
        blob: response.data,
        metadata: parsedMetadata,
      };
    })
    .catch(async (error) => {
      // Handle blob error responses
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          error.response.data = errorData;
        } catch (e) {
          // If we can't parse it, leave it as is
        }
      }
      throw error;
    });
};
export const importPaymentUpdate = (
  data: any,
  accessToken: string,
  options?: { timeoutMs?: number }
) => {
  return importAxios
    .post(process.env.REACT_APP_API_URL + url.IMPORT_PAYMENT_UPDATE, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      timeout: options?.timeoutMs ?? 5 * 60 * 1000,
      responseType: "blob",
    })
    .then((response) => {
      // Get the X-Metadata header if it exists
      const metadata = response.headers["x-metadata"];
      let parsedMetadata = null;

      // Try to parse the metadata if it exists
      if (metadata) {
        try {
          parsedMetadata = JSON.parse(metadata);
        } catch (e) {
          parsedMetadata = metadata;
        }
      }

      // Return both the blob and metadata
      return {
        blob: response.data,
        metadata: parsedMetadata,
      };
    })
    .catch(async (error) => {
      // Handle blob error responses
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          error.response.data = errorData;
        } catch (e) {
          // If we can't parse it, leave it as is
        }
      }
      throw error;
    });
};
export const updatePayment = (id: any, data: any) =>
  api.put(url.UPDATE_PAYMENT + "/" + id, data);
export const deletePayment = (id: any) =>
  api.delete(url.DELETE_PAYMENT + "/" + id);
export const deleteAllPayments = (params: any) =>
  api.delete(url.DELETE_ALL_PAYMENTS, params);
export const exportPaymentsToCsv = (params: any) =>
  api.get(url.EXPORT_PAYMENTS_TO_CSV, params, {
    responseType: "blob",
  });
export const exportAgencyPaymentsToCsv = (params: any) =>
  api.get(url.EXPORT_AGENCY_PAYMENTS_TO_CSV, params, {
    responseType: "blob",
  });
export const getTotalPaymentsAmount = (params: any) =>
  api.get(url.GET_TOTAL_PAYMENTS_AMOUNT, params);
export const getTotalPaymentsCount = (params: any) =>
  api.get(url.GET_TOTAL_PAYMENTS_COUNT, params);
export const getPaymentsAgencyCount = (params: any) =>
  api.get(url.GET_PAYMENTS_AGENCY_COUNT, params);
export const getPaymentsMdaCount = (params: any) =>
  api.get(url.GET_PAYMENTS_MDA_COUNT, params);
export const getPaymentsPerCategory = (params: any) =>
  api.get(url.GET_PAYMENTS_PER_CATEGORY, params);
export const getPaymentsSalaryAligned = (params: any) =>
  api.get(url.GET_PAYMENTS_SALARY_ALIGNED_DISTRIBUTION, params);
export const getPaymentsRegionCategoryDistribution = (params: any) =>
  api.get(url.GET_PAYMENTS_REGION_CATEGORY_DISTRIBUTION, params);
export const getPaymentsAgencyCategoryDistribution = (params: any) =>
  api.get(url.GET_PAYMENTS_AGENCY_CATEGORY_DISTRIBUTION, params);
export const getPaymentsRegionGenderDistribution = (params: any) =>
  api.get(url.GET_PAYMENTS_REGION_GENDER_DISTRIBUTION, params);
export const getPaymentsGenderCategoryDistribution = (params: any) =>
  api.get(url.GET_PAYMENTS_GENDER_CATEGORY_DISTRIBUTION, params);
export const getPaymentsGenderDistribution = (params: any) =>
  api.get(url.GET_PAYMENTS_GENDER_DISTRIBUTION, params);
export const getPaymentsThematicAreaDistribution = (params: any) =>
  api.get(url.GET_PAYMENTS_THEMATIC_AREA_DISTRIBUTION, params);
export const getPaymentTrendByCategory = (params: any) =>
  api.get(url.GET_PAYMENT_TREND_BY_CATEGORY, params);

// Position Types
export const getPositionTypes = (params: any) =>
  api.get(url.GET_POSITION_TYPES, params);
export const getPositionType = (id: any, params: any) =>
  api.get(url.GET_POSITION_TYPE + "/" + id, params);
export const addPositionType = (data: any) =>
  api.create(url.ADD_POSITION_TYPE, data);
export const updatePositionType = (id: any, data: any) =>
  api.put(url.UPDATE_POSITION_TYPE + "/" + id, data);
export const deletePositionType = (id: any) =>
  api.delete(url.DELETE_POSITION_TYPE + "/" + id);

// Payment Categories
export const getPaymentCategories = (params: any) =>
  api.get(url.GET_PAYMENT_CATEGORIES, params);
export const getPaymentCategory = (id: any, params: any) =>
  api.get(url.GET_PAYMENT_CATEGORY + "/" + id, params);
export const addPaymentCategory = (data: any) =>
  api.create(url.ADD_PAYMENT_CATEGORY, data);
export const updatePaymentCategory = (data: any) =>
  api.update(url.UPDATE_PAYMENT_CATEGORY + "/" + data.id, data);
export const deletePaymentCategory = (id: any) =>
  api.delete(url.DELETE_PAYMENT_CATEGORY + "/" + id);

// Agency Payments
export const getAgencyPayments = (params: any) =>
  api.get(url.GET_AGENCY_PAYMENTS, params);
export const getAgencyPayment = (id: any, params: any) =>
  api.get(url.GET_AGENCY_PAYMENT + "/" + id, params);
export const addAgencyPayment = (data: any) =>
  api.create(url.ADD_AGENCY_PAYMENT, data);
export const updateAgencyPayment = (id: any, data: any) =>
  api.put(url.UPDATE_AGENCY_PAYMENT + "/" + id, data);
export const deleteAgencyPayment = (id: any) =>
  api.delete(url.DELETE_AGENCY_PAYMENT + "/" + id);

//  Overlapping Payments
export const getOverlappingPayments = (params: any) =>
  api.get(url.GET_OVERLAPPING_PAYMENTS, params);
export const getOverlappingPayment = (id: any, params: any) =>
  api.get(url.GET_OVERLAPPING_PAYMENT + "/" + id, params);
export const addOverlappingPayment = (data: any) =>
  api.create(url.ADD_OVERLAPPING_PAYMENT, data);
export const updateOverlappingPayment = (id: any, data: any) =>
  api.put(url.UPDATE_OVERLAPPING_PAYMENT + "/" + id, data);
export const deleteOverlappingPayment = (id: any) =>
  api.delete(url.DELETE_OVERLAPPING_PAYMENT + "/" + id);
export const syncOverlappingPayments = () =>
  api.get(url.SYNC_OVERLAPPING_PAYMENTS);
export const startOverlappingPaymentsSync = () =>
  api.create(url.START_OVERLAPPING_PAYMENTS_SYNC, {});
export const getOverlappingPaymentsSyncStatus = (jobId: string) =>
  api.get(url.OVERLAPPING_PAYMENTS_SYNC_STATUS + "/" + jobId, {});
/** Every status except double entry, which goes through the merge below. */
export const resolveOverlappingPayments = (data: {
  ids: Array<string | number>;
  overlapStatus: string;
  comments?: string;
}) => api.create(url.RESOLVE_OVERLAPPING_PAYMENTS, { data });
/** Both payments of a pair, for choosing which values survive a merge. */
export const getOverlappingPaymentMergeCandidates = (id: string | number) =>
  api.get(`${url.MERGE_OVERLAPPING_PAYMENT}/${id}/merge`);
/** Resolves a pair as a double entry by merging its payments into one. */
export const mergeOverlappingPayment = (
  id: string | number,
  data: {
    selections: Record<string, "payment" | "overlappingPayment">;
    comments?: string;
  }
) => api.create(`${url.MERGE_OVERLAPPING_PAYMENT}/${id}/merge`, { data });
export const exportOverlappingPaymentsToCsv = (params: any) =>
  api.get(url.EXPORT_OVERLAPPING_PAYMENTS_TO_CSV, params, {
    responseType: "blob",
  });
export const getAgencyOverlappingPayments = (params: any) =>
  api.get(url.GET_AGENCY_OVERLAPPING_PAYMENTS, params);
export const getAgencyOverlappingPayment = (id: any, params: any) =>
  api.get(url.GET_AGENCY_OVERLAPPING_PAYMENT + "/" + id, params);
export const exportAgencyOverlappingPaymentsToCsv = (params: any) =>
  api.get(url.EXPORT_AGENCY_OVERLAPPING_PAYMENTS_TO_CSV, params, {
    responseType: "blob",
  });
